import Api from '@/api';
import { useQuery, useMutation, useQueryClient, QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { ProfileType } from './types';
import { useToast } from '@/context/toast/ToastContext';

export const PROFILE_QUERY_KEY = ['profile'];

export const useProfile = (
  options: Omit<UseQueryOptions<ProfileType, Error, ProfileType, QueryKey>, 'queryKey' | 'queryFn'> = {},
) => {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () => Api.getProfile(),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { handleToastOpen } = useToast();

  return useMutation({
    mutationFn: async (partialPayload: Partial<ProfileType>) => {
      const currentProfile = queryClient.getQueryData<ProfileType>(PROFILE_QUERY_KEY);

      if (!currentProfile) {
        throw new Error('Profile not yet loaded');
      }

      const fullPayload: ProfileType = {
        ...currentProfile,
        ...partialPayload,
      };

      return Api.updateProfile(fullPayload);
    },

    onMutate: async (partialPayload) => {
      await queryClient.cancelQueries({ queryKey: PROFILE_QUERY_KEY });
      const previousProfile = queryClient.getQueryData<ProfileType>(PROFILE_QUERY_KEY);

      if (previousProfile) {
        queryClient.setQueryData<ProfileType>(PROFILE_QUERY_KEY, {
          ...previousProfile,
          ...partialPayload,
        });
      }

      return { previousProfile };
    },

    onError: (err, partialPayload, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(PROFILE_QUERY_KEY, context.previousProfile);
      }
      handleToastOpen({
        type: 'error',
        title: `${err.message}. Couldn't update profile. Please try again`,
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};
